import { createFileRoute, Link } from "@tanstack/react-router";
import { ComingUpLive } from "@/components/events/ComingUpLive";

import {
  Wind,
  NotebookPen,
  BookOpen,
  ClipboardList,
  Users,
  Sparkles,
  Info,
  ShieldAlert,
  ArrowRight,
  Loader2,
  Brain,
  Leaf,
  Heart,
  Flower2,
  Moon,
  Eye,
  CalendarDays,
  MessagesSquare,
  Anchor,
  Smartphone,
  GraduationCap,
  Compass,
  Clock,
  MapPin,
  HandHeart,
} from "lucide-react";
import { useEffect, useRef } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMembership } from "@/hooks/useMembership";
import { trackFunnel, startMembershipJourney, MEMBERSHIP_JOURNEY_PATH } from "@/lib/funnel";
import { PlanCarousel } from "@/components/membership/PlanCarousel";
import { setSelectedPlanId } from "@/lib/selected-plan";
import type { MembershipPlan } from "@/lib/membership-plans";
import {
  trackPlanSelected,
  useScrollDepth,
  useSectionInView,
} from "@/lib/pricing-analytics";
import { TrustpilotStrip } from "@/components/TrustpilotStrip";
import founderImage from "@/assets/founder-jana.jpg";
import dashboardShot from "@/assets/guest-preview/dashboard.jpg";
import libraryShot from "@/assets/peek-real/library.jpg.asset.json";
import breathingShot from "@/assets/peek-real/breathing.jpg.asset.json";
import groundingShot from "@/assets/grounding-covers/54321-senses-cover.png.asset.json";
import somaticShot from "@/assets/peek-real/somatic.jpg.asset.json";
import journalShot from "@/assets/peek-real/journal.jpg.asset.json";
import dreamsShot from "@/assets/peek-real/dreams.jpg.asset.json";
import communityShot from "@/assets/peek-real/community.jpg.asset.json";
import eftShot from "@/assets/peek-real/eft-tapping.jpg.asset.json";
import eventsShot from "@/assets/peek-journey/events.png.asset.json";
import wotShot from "@/assets/peek-journey/wot.png.asset.json";
import glossaryShot from "@/assets/peek-journey/glossary.png.asset.json";
import heroIllustration from "@/assets/home/inspiration-path.jpg.asset.json";

/**
 * /membership-benefits — the public "Membership / What's included" conversion
 * page. Anyone can read it; the calls to action adapt to whether the reader is
 * a guest, mid-onboarding, or already an active member.
 */

const FAQS: { q: string; a: string }[] = [
  {
    q: "What is included?",
    a: "Everything on this page: the Trauma Recovery Library, guided regulation tools (breathing, grounding, somatic practices, EFT tapping, meditation, sleep support), the Reflective Recovery Journal, Dream Journal, Shadow Work and inner child exercises, educational self-assessments, the member community, Ask Recovery Trauma™, live workshops and events, plus everything new that is published while you are a member.",
  },
  {
    q: "Can I cancel anytime?",
    a: "Yes. Membership is $14.99 USD per month and you can cancel at any time from your billing page. You keep access until the end of the period you have paid for, and there is no cancellation fee.",
  },
  {
    q: "Is this therapy?",
    a: "No. Recovery Trauma™ is an educational and peer-support platform created from lived experience. It does not provide medical or psychological treatment, it is not a diagnosis, and it is not a crisis service. Many members use it alongside therapy, and our therapist directory is free to browse.",
  },
  {
    q: "Who is Recovery Trauma™ for?",
    a: "Recovery Trauma™ is for adults who want to better understand themselves, their experiences and their nervous system. Whether you're navigating trauma, difficult childhood experiences, relationship or attachment wounds, emotional triggers, chronic stress, or simply want practical tools to support your wellbeing and personal growth, you can explore the resources at your own pace.",
  },
  {
    q: "How often is new content added?",
    a: "New articles, guided practices and workshops are added every week, and your membership always includes everything published.",
  },
  {
    q: "Can I access it on mobile?",
    a: "Yes. Recovery Trauma™ works in any browser on phone, tablet and desktop, and you can add it to your home screen. Your journals, favourites and progress follow you across every device.",
  },
];

export const Route = createFileRoute("/membership-benefits")({
  head: () => ({
    meta: [
      { title: "Membership — Everything Included | Recovery Trauma™" },
      {
        name: "description",
        content:
          "See everything included in a Recovery Trauma™ membership: trauma education, grounding and breathing tools, journals, shadow work, community, live workshops and weekly new content.",
      },
      {
        property: "og:title",
        content: "Membership — Everything Included | Recovery Trauma™",
      },
      {
        property: "og:description",
        content:
          "Trauma-informed education, guided exercises, journals, community and live workshops — everything your Recovery Trauma™ membership opens up.",
      },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Recovery Trauma™" },
      { property: "og:locale", content: "en_GB" },
      {
        property: "og:url",
        content: "https://recoverytrauma.com/membership-benefits",
      },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "Membership — Everything Included | Recovery Trauma™",
      },
      {
        name: "twitter:description",
        content:
          "Trauma-informed education, guided exercises, journals, community and live workshops — everything your Recovery Trauma™ membership opens up.",
      },
    ],
    links: [
      { rel: "canonical", href: "https://recoverytrauma.com/membership-benefits" },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Recovery Trauma™ Membership",
          description:
            "Membership access to Recovery Trauma™: guided recovery tools, journals, trauma education, self-assessments, live workshops and the member community.",
          brand: { "@type": "Brand", name: "Recovery Trauma™" },
          url: "https://recoverytrauma.com/membership-benefits",
          offers: {
            "@type": "Offer",
            price: "14.99",
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            url: "https://recoverytrauma.com/membership",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map(({ q, a }) => ({
            "@type": "Question",
            name: q,
            acceptedAnswer: { "@type": "Answer", text: a },
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            {
              "@type": "ListItem",
              position: 1,
              name: "Home",
              item: "https://recoverytrauma.com/",
            },
            {
              "@type": "ListItem",
              position: 2,
              name: "Membership",
              item: "https://recoverytrauma.com/membership-benefits",
            },
          ],
        }),
      },
    ],
  }),
  component: MembershipBenefitsPage,
});

/* ─────────────────────────── Content ─────────────────────────── */

type Pillar = {
  emoji: string;
  title: string;
  line: string;
  items: string[];
  image: string;
  alt: string;
  tint: string;
};

const PILLARS: Pillar[] = [
  {
    emoji: "🧠",
    title: "Understand",
    line: "Learn what trauma is and how it affects your mind and body.",
    items: [
      "Trauma Recovery Library",
      "PTSD & Complex PTSD education",
      "Attachment styles",
      "Nervous system education",
      "Emotional regulation",
      "Relationships",
      "Boundaries",
      "Educational assessments",
      "…and much more",
    ],
    image: libraryShot.url,
    alt: "The Recovery Trauma™ Trauma Recovery Library inside the membership",
    tint: "from-amber-100/60 to-amber-50/30",
  },
  {
    emoji: "🌿",
    title: "Regulate",
    line: "Build skills to calm your nervous system and feel safer in your body.",
    items: [
      "Breathing exercises",
      "Grounding exercises",
      "Somatic practices",
      "EFT Tapping",
      "Meditation",
      "Sleep & relaxation",
      "Crisis support tools",
      "…and much more",
    ],
    image: groundingShot.url,
    alt: "The 5-4-3-2-1 senses grounding exercise inside Recovery Trauma™",
    tint: "from-emerald-100/60 to-emerald-50/30",
  },
  {
    emoji: "💛",
    title: "Process",
    line: "Reflect on your experiences in a safe and compassionate way.",
    items: [
      "Reflective Recovery Journal",
      "Dream Journal",
      "Shadow Work",
      "Inner child exercises",
      "Guided prompts",
      "Creative reflection & drawing board",
      "…and much more",
    ],
    image: journalShot.url,
    alt: "The Reflective Recovery Journal with guided prompts",
    tint: "from-rose-100/60 to-rose-50/30",
  },
  {
    emoji: "🌱",
    title: "Integrate",
    line: "Continue growing with support, education and connection.",
    items: [
      "Community",
      "Live workshops",
      "Recovery events",
      "Daily routines & daily support",
      "Meaning & purpose",
      "Healthy relationships",
      "Joy & play",
      "Nature connection & forest bathing",
      "Gardening",
      "Aromatherapy & relaxation practices",
      "Creative expression",
      "Weekly new content",
      "Ask Recovery Trauma™",
      "Ongoing platform updates",
      "…and much more",
    ],
    image: communityShot.url,
    alt: "Members supporting one another in the Recovery Trauma™ community",
    tint: "from-sky-100/60 to-sky-50/30",
  },

];




const STEPS = [
  {
    n: 1,
    emoji: "🧠",
    title: "Understand",
    line: "Learn about trauma",
  },
  { n: 2, emoji: "🌿", title: "Regulate", line: "Calm your nervous system" },
  { n: 3, emoji: "💛", title: "Process", line: "Reflect at your own pace" },
  { n: 4, emoji: "🌱", title: "Integrate", line: "Build long-term recovery" },
] as const;

const WHY: { icon: React.ComponentType<{ className?: string }>; text: string }[] = [
  { icon: BookOpen, text: "Trauma-informed resources gathered in one calm place" },
  { icon: Clock, text: "Learn at your own pace — nothing is timed or scored" },
  { icon: Leaf, text: "Practical exercises you can use in everyday life" },
  { icon: Users, text: "A supportive, moderated community" },
  { icon: Sparkles, text: "New resources added every week" },
  { icon: MapPin, text: "Access anywhere, on any device" },
  { icon: HandHeart, text: "Created by someone with lived experience" },
];

/* ─────────────────────────── CTA state ─────────────────────────── */

function scrollToIncluded(e: React.MouseEvent) {
  e.preventDefault();
  const el =
    document.getElementById("recovery-journey") ??
    document.getElementById("whats-included");
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
}

function MembershipBenefitsPage() {
  const { loading, isAuthed, isMember, isAdmin } = useMembership();
  const navigate = useNavigate();

  // Funnel step 1 — the public membership offer was seen.
  const viewedRef = useRef(false);
  useEffect(() => {
    if (loading || viewedRef.current) return;
    viewedRef.current = true;
    trackFunnel(
      "membership_viewed",
      { source: "membership_benefits" },
      { authed: isAuthed, is_member: isMember, is_admin: isAdmin },
    );
  }, [loading, isAuthed, isMember, isAdmin]);
  const hasAccess = isMember || isAdmin;

  // Measurement for this landing page: prices in view, and reading depth.
  const priceRef = useRef<HTMLElement>(null);
  useSectionInView(priceRef, "price_section_viewed", {}, "membership_benefits");
  useScrollDepth("membership_benefits");

  const choosePlan = (plan: MembershipPlan) => {
    setSelectedPlanId(plan.id);
    trackPlanSelected(plan.id, plan.priceCents / 100, "membership_benefits");
    navigate({
      to: isAuthed ? "/complete-membership" : "/signup",
      search: { plan: plan.id },
    });
  };

  // Pay-first flow: anyone without an active membership (guest OR signed-in
  // unpaid) goes straight to direct checkout. Post-payment linking attaches
  // the payment to the existing account, so no duplicate account is created.
  const primaryCta = loading ? (
    <span className="inline-flex min-h-[56px] items-center justify-center rounded-full bg-[var(--gradient-gold)] px-8 py-4 text-base font-semibold text-foreground shadow-[var(--shadow-gold)]">
      <Loader2 className="h-5 w-5 animate-spin" />
      <span className="sr-only">Loading your membership options</span>
    </span>
  ) : hasAccess ? (
    <Link
      to="/dashboard"
      data-testid="benefits-cta"
      className="inline-flex min-h-[56px] max-w-full items-center justify-center gap-2 text-balance rounded-full bg-[var(--gradient-gold)] px-8 py-4 text-center text-base font-semibold text-foreground shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5"
    >
      Explore your membership
      <ArrowRight className="h-5 w-5 flex-none" />
    </Link>
  ) : (
    <Link
      to={MEMBERSHIP_JOURNEY_PATH}
      data-testid="benefits-cta"
      onClick={() => startMembershipJourney("benefits_hero", "membership_benefits")}

      className="inline-flex min-h-[60px] w-full max-w-full items-center justify-center gap-2 text-balance rounded-full bg-[var(--gradient-gold)] px-8 py-4 text-center font-display text-[17px] font-semibold text-foreground shadow-[var(--shadow-gold)] transition hover:-translate-y-0.5 sm:w-auto sm:px-10 sm:text-lg"
    >
      Join Recovery Trauma™
      <ArrowRight className="h-5 w-5 flex-none" />
    </Link>
  );

  return (
    <div className="min-h-dvh bg-gradient-to-b from-[oklch(0.985_0.012_85)] via-background to-[oklch(0.96_0.03_82)]">
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="px-4 pt-12 sm:pt-16">
        <div className="mx-auto grid max-w-5xl items-center gap-8 md:grid-cols-2">
          <div className="text-center md:text-start">
            <span className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-cream/70 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--gold-deep)]">
              <Sparkles className="h-3.5 w-3.5" /> Membership
            </span>
            <h1 className="mt-4 font-serif text-3xl leading-tight text-foreground sm:text-4xl md:text-[2.75rem]">
              Everything you need to support your recovery
            </h1>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Recovery Trauma™ provides a growing collection of trauma-informed
              tools, education, guided exercises and community support designed
              to support your wellbeing — one gentle step at a time.
            </p>
            {!hasAccess && (
              <p className="mt-3 text-sm font-medium text-[var(--gold-deep)]">
                Three membership options — $14.99 monthly, $59.99 for 6 months
                ($10/month) or $89.99 yearly ($7.50/month).{" "}
                <a
                  href="#membership-options"
                  className="underline underline-offset-4"
                >
                  See the options
                </a>
              </p>
            )}
            <div className="relative z-10 mt-7 flex flex-col items-center gap-3 md:items-start">
              {primaryCta}
              {!hasAccess && (
                <p className="text-xs text-muted-foreground">
                  Cancel anytime · Immediate access after joining
                </p>
              )}
              <button
                type="button"
                onClick={scrollToIncluded}
                data-testid="benefits-see-included"
                className="inline-flex min-h-[48px] w-full items-center justify-center rounded-full border border-gold/40 bg-card/70 px-6 py-3 text-sm font-semibold text-foreground transition hover:-translate-y-0.5 sm:w-auto"
              >
                See Everything Included ↓
              </button>
            </div>
          </div>
          <div className="relative">
            <img
              src={heroIllustration.url}
              alt="A gentle illustrated path winding towards the light, representing the Recovery Trauma™ journey"
              loading="eager"
              className="mx-auto w-full max-w-md rounded-[2rem] border border-gold/25 object-cover shadow-[0_24px_60px_-38px_rgba(140,105,40,0.6)]"
            />
          </div>
        </div>
      </section>

      {/* ── Membership options — visible without leaving the page ───── */}
      {!hasAccess && (
        <section
          id="membership-options"
          aria-labelledby="membership-options-heading"
          className="px-4 pt-10 sm:pt-14"
          ref={priceRef}
        >
          <div className="mx-auto max-w-5xl">
            <div className="text-center">
              <h2
                id="membership-options-heading"
                className="font-serif text-2xl text-foreground sm:text-3xl"
              >
                Membership options
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
                Same full access on every plan — simply choose how often you
                would like to pay.
              </p>
            </div>
            <PlanCarousel onChoose={choosePlan} source="membership_benefits" />
          </div>
        </section>
      )}




      {/* ── Recovery journey (four pillars) ──────────────────── */}
      <section
        id="recovery-journey"
        aria-labelledby="recovery-journey-heading"
        className="px-4 py-14 sm:py-20"
      >
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <h2
              id="recovery-journey-heading"
              className="font-serif text-2xl text-foreground sm:text-3xl"
            >
              Your recovery journey
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
              Your membership is organised around four gentle areas. Begin
              wherever feels right for you today.
            </p>
          </div>

          <div className="mt-10 space-y-8">
            {PILLARS.map((p, i) => (
              <article
                key={p.title}
                className={`grid items-center gap-6 rounded-[2rem] border border-gold/25 bg-gradient-to-br ${p.tint} p-5 shadow-[0_18px_50px_-38px_rgba(140,105,40,0.5)] sm:p-7 md:grid-cols-2`}
              >
                <div className={i % 2 === 1 ? "md:order-2" : undefined}>
                  <h3 className="font-display text-xl text-foreground sm:text-2xl">
                    <span aria-hidden className="me-2">
                      {p.emoji}
                    </span>
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground/80 sm:text-base">
                    {p.line}
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {p.items.map((it) => (
                      <li
                        key={it}
                        className="rounded-full border border-gold/30 bg-background/70 px-3 py-1 text-xs font-medium text-foreground/80"
                      >
                        {it}
                      </li>
                    ))}
                  </ul>
                </div>
                <img
                  src={p.image}
                  alt={p.alt}
                  loading="lazy"
                  className={`w-full rounded-2xl border border-gold/25 object-cover shadow-[0_16px_40px_-30px_rgba(140,105,40,0.6)] ${
                    i % 2 === 1 ? "md:order-1" : ""
                  }`}
                />
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── Next live member event ───────────────────────────── */}
      <div className="px-4 pb-14 sm:pb-20">
        <ComingUpLive source="membership_benefits" />
      </div>


      {/* ── How it works ─────────────────────────────────────── */}
      <section aria-labelledby="how-it-works-heading" className="px-4 pb-14 sm:pb-20">
        <div className="mx-auto max-w-5xl rounded-[2rem] border border-gold/25 bg-card/80 p-6 shadow-[0_18px_50px_-38px_rgba(140,105,40,0.5)] sm:p-10">
          <h2
            id="how-it-works-heading"
            className="text-center font-serif text-2xl text-foreground sm:text-3xl"
          >
            How Recovery Trauma™ works
          </h2>
          <ol className="relative mt-10 grid gap-6 md:grid-cols-4">
            <span
              aria-hidden
              className="pointer-events-none absolute start-0 end-0 top-7 hidden h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent md:block"
            />
            {STEPS.map((s) => (
              <li key={s.n} className="relative text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[var(--gradient-gold)] text-xl shadow-[var(--shadow-gold)] ring-4 ring-background">
                  <span aria-hidden>{s.emoji}</span>
                </span>
                <p className="mt-3 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--gold-deep)]">
                  Step {s.n}
                </p>
                <h3 className="mt-1 font-display text-lg text-foreground">
                  {s.title}
                </h3>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
                  {s.line}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ── Why members join ─────────────────────────────────── */}
      <section aria-labelledby="why-join-heading" className="px-4 pb-14 sm:pb-20">
        <div className="mx-auto max-w-4xl">
          <h2
            id="why-join-heading"
            className="text-center font-serif text-2xl text-foreground sm:text-3xl"
          >
            Why members join
          </h2>
          <ul className="mt-8 grid gap-3 sm:grid-cols-2">
            {WHY.map(({ icon: Icon, text }) => (
              <li
                key={text}
                className="flex items-start gap-3 rounded-2xl border border-gold/25 bg-card/80 px-4 py-3"
              >
                <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-cream/70 text-[var(--gold-deep)] dark:bg-cream/10">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="text-sm leading-relaxed text-foreground/85">
                  {text}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* ── Founder ──────────────────────────────────────────── */}
      <section aria-labelledby="founder-heading" className="px-4 pb-14 sm:pb-20">
        <div className="mx-auto grid max-w-4xl items-center gap-6 rounded-[2rem] border border-gold/25 bg-card/85 p-6 shadow-[0_18px_50px_-38px_rgba(140,105,40,0.5)] sm:p-10 md:grid-cols-[220px_1fr]">
          <img
            src={founderImage}
            alt="Jana Burkhardt, founder of Recovery Trauma™"
            loading="lazy"
            className="mx-auto w-40 rounded-3xl border border-gold/30 object-cover shadow-[var(--shadow-soft)] md:w-full"
          />
          <div>
            <h2
              id="founder-heading"
              className="font-serif text-2xl text-foreground sm:text-3xl"
            >
              Why I created Recovery Trauma™
            </h2>
            <div className="mt-3 space-y-3 text-sm leading-relaxed text-foreground/85 sm:text-base">
              <p>
                Recovery can be overwhelming — especially when helpful
                information, practical tools and support are scattered across so
                many different places.
              </p>
              <p>
                I created Recovery Trauma™ to bring those things together in one
                calm, supportive space. A place where you can learn about trauma,
                understand your patterns, explore practical tools, join live
                experiences and move through recovery at your own pace.
              </p>
              <p>
                Recovery Trauma™ is shaped by lived experience and built around
                one simple belief: support should feel accessible, compassionate
                and human.
              </p>
              <p className="font-display text-base text-[var(--gold-deep)]">
                💛 Jana Burkhardt, Founder
              </p>
            </div>
            <Link
              to="/about"
              className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[var(--gold-deep)] underline-offset-4 hover:underline"
            >
              Meet the founder <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Testimonials / Trustpilot ────────────────────────── */}
      <section aria-labelledby="reviews-heading" className="px-4 pb-14 sm:pb-20">
        <div className="mx-auto max-w-4xl">
          <h2
            id="reviews-heading"
            className="text-center font-serif text-2xl text-foreground sm:text-3xl"
          >
            What members say
          </h2>
          <div className="mt-8">
            <TrustpilotStrip />
          </div>
          <div className="mt-6 text-center">
            <Link
              to="/testimonials"
              className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--gold-deep)] underline-offset-4 hover:underline"
            >
              Read member testimonials <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────── */}
      <section aria-labelledby="faq-heading" className="px-4 pb-14 sm:pb-20">
        <div className="mx-auto max-w-3xl">
          <h2
            id="faq-heading"
            className="text-center font-serif text-2xl text-foreground sm:text-3xl"
          >
            Membership questions
          </h2>
          <div className="mt-8 space-y-3">
            {FAQS.map(({ q, a }) => (
              <details
                key={q}
                className="group rounded-2xl border border-gold/25 bg-card/85 px-5 py-4"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-3 text-sm font-semibold text-foreground sm:text-base">
                  {q}
                  <ArrowRight className="h-4 w-4 flex-none text-[var(--gold-deep)] transition group-open:rotate-90" />
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                  {a}
                </p>
              </details>
            ))}
          </div>

          <div className="mt-8 space-y-3">
            <p className="flex items-start gap-2 rounded-2xl border border-gold/35 bg-cream/60 px-4 py-3 text-sm leading-relaxed text-foreground dark:bg-cream/10">
              <Info className="mt-0.5 h-4 w-4 flex-none text-[var(--gold-deep)]" />
              <span>
                New tools, articles and practices are added regularly — your
                membership always includes everything published.
              </span>
            </p>
            <p className="flex items-start gap-2 rounded-2xl border border-border bg-muted/40 px-4 py-3 text-sm leading-relaxed text-foreground/85">
              <ShieldAlert className="mt-0.5 h-4 w-4 flex-none text-muted-foreground" />
              <span>
                Recovery Trauma™ is an educational and peer-support platform. It
                is not medical or psychological treatment, and it is not a crisis
                service. If you are in danger or in crisis, please contact your
                local emergency services or a crisis line.
              </span>
            </p>
          </div>
        </div>
      </section>

      {/* ── Final CTA ────────────────────────────────────────── */}
      <section aria-labelledby="final-cta-heading" className="px-4 pb-16 sm:pb-24">
        <div className="mx-auto max-w-3xl rounded-[2rem] border border-gold/30 bg-card/90 p-7 text-center shadow-[0_20px_60px_-40px_rgba(140,105,40,0.6)] sm:p-10">
          <h2
            id="final-cta-heading"
            className="font-serif text-2xl text-foreground sm:text-3xl"
          >
            Ready to begin your recovery journey?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-muted-foreground sm:text-base">
            Join Recovery Trauma™ today and gain unlimited access to every tool,
            resource, workshop and community feature designed to support your
            wellbeing.
          </p>
          <div className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row">
            {primaryCta}
          </div>

          <p className="mt-6 text-xs text-muted-foreground">
            Healing Beyond Survival Mode™
          </p>
        </div>
      </section>
    </div>
  );
}
